import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import MypageNavbar, { ProfileStatus } from '../MypageNavbar';
import MypageFooter from '../MypageFooter';
import ChangePasswordDialog from '../ChangePasswordDialog';
import CouponDialog from '../CouponDialog';
import { SESSION_SNOOZE_KEY } from '../ProfileGuardDialog';
import UnifiedConfirmDialog from '../UnifiedConfirmDialog';

interface Props {
  children: React.ReactNode;
  title: string;
  profileStatus: ProfileStatus;
  // Banner goes before the container
  topBanner?: React.ReactNode;
  hideNavbar?: boolean;
  customHeader?: React.ReactNode;
}

const MypageLayout: React.FC<Props> = ({ children, title, profileStatus, topBanner, hideNavbar, customHeader }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  
  const [showPwdDlg, setShowPwdDlg] = useState(false);
  const [showCouponDlg, setShowCouponDlg] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  return (
    <div className="flex-1 w-full bg-slate-50 font-sans flex flex-col pb-6 sm:pb-8 pt-2 sm:pt-4 overflow-x-hidden">
      <Helmet>
        <title>{title} - Melodis Clinic</title>
      </Helmet>

      {topBanner}

      <div className="max-w-3xl mx-auto px-0 sm:px-6 w-full flex-1 flex flex-col sm:justify-start sm:pt-8 sm:pb-12">
        <div className="bg-white rounded-[2rem] sm:rounded-[2.5rem] border-none sm:border border-slate-100 shadow-none sm:shadow-sm relative overflow-x-hidden flex-1 sm:flex-initial flex flex-col min-h-[440px]">
          
          {!hideNavbar && (
            <MypageNavbar
              profileStatus={profileStatus}
              onClickChangePassword={() => setShowPwdDlg(true)}
              onClickOpenCoupons={() => setShowCouponDlg(true)}
            />
          )}

          {customHeader}

          <div className="px-6 py-4 sm:py-6 md:py-8 flex-1 flex flex-col overflow-visible">
            {children}
          </div>

          <div className="px-6 pb-4 sm:pb-6 md:pb-8 mt-auto">
            <MypageFooter
              onClickChangePassword={() => setShowPwdDlg(true)}
              onClickOpenCoupons={() => setShowCouponDlg(true)}
              onLogout={() => setShowLogoutConfirm(true)}
            />
          </div>
        </div>
      </div>

      <ChangePasswordDialog open={showPwdDlg} onClose={() => setShowPwdDlg(false)} />
      <CouponDialog open={showCouponDlg} onClose={() => setShowCouponDlg(false)} />

      <UnifiedConfirmDialog
        open={showLogoutConfirm}
        onClose={() => setShowLogoutConfirm(false)}
        type="logout"
        title={t('auth.logout_confirm') || t('common.logout') || 'ログアウト'}
        message={t('auth.logout_hint') || t('mypage.logout_confirm') || 'ログアウトしてもよろしいですか？'}
        onConfirm={() => {
          setShowLogoutConfirm(false);
          try {
            sessionStorage.removeItem('snooze_profile_alert');
          } catch { }
          logout();
          navigate('/login');
        }}
      />
    </div>
  );
};

export default MypageLayout;
